// Playback: turning a stream of snapshots that arrive unevenly into motion that
// does not.
//
// The server produces a snapshot every SERVER_TICK_MS exactly, and stamps each
// one with the tick it belongs to. What the network then does to their *arrival*
// is not the server's doing and must not be visible: measured against
// production, only two thirds of snapshots landed on time — median 102ms, p95
// 166ms, max 189ms, with bursts under 50ms in between.
//
// Two ideas do all the work:
//
//   Time spans come from tick numbers, never from arrival times. Two snapshots
//   one tick apart are one tick apart however unevenly they turned up, so a
//   burst cannot compress the animation and a straggler cannot stretch it.
//
//   Drawing runs deliberately behind the newest snapshot. That delay is the
//   slack: a packet has to be later than the whole delay before anything shows,
//   instead of later than a single tick. It costs a constant lag, which is the
//   trade the simulation already makes elsewhere — a fixed delay goes unnoticed,
//   variance is what reads as jerky.

export const SERVER_TICK_MS = 100;
export const RENDER_DELAY_MS = 200;

const DEFAULTS = {
  tickMs: SERVER_TICK_MS,
  delayMs: RENDER_DELAY_MS,
  // Snapshots kept. Only the two straddling the render clock are ever drawn;
  // the rest is slack for a late burst.
  limit: 8,
  // A jump further than this between two snapshots is a teleport — a reset or a
  // respawn — and is not something to slide a sprite across the map for.
  snapDistance: 60,
  // Clock error beyond this is corrected by jumping rather than easing. That is
  // a reconnection or a long stall, where easing would take seconds.
  snapTicks: 4,
  // Error smaller than this is left completely alone.
  //
  // This deadband matters more than it looks. The error is measured the instant
  // a snapshot lands, so it carries that snapshot's arrival jitter even when the
  // two clocks agree perfectly. Correcting on every arrival would therefore feed
  // the jitter straight back into playback and undo the buffer. Genuine drift
  // accumulates in one direction and crosses this eventually; jitter does not.
  deadbandTicks: 1,
  // Fraction of a real error corrected per snapshot once the deadband is
  // crossed: invisible per step, but enough that drift cannot accumulate.
  ease: 0.08,
};

export const createPlayback = (options = {}) => {
  const config = { ...DEFAULTS, ...options };
  // Snapshots, oldest first: { tick, positions }. positions maps a unit key to
  // {x, y}.
  let frames = [];
  // Maps local time onto the server's tick timeline: server tick `originTick`
  // was being drawn at local time `originAt`.
  let clock = null;

  const reset = () => {
    frames = [];
    clock = null;
  };

  // Where playback is now, as a fractional server tick, or null before the
  // first snapshot has established the timeline.
  const tickAt = (nowMs) =>
    clock === null
      ? null
      : clock.originTick + (nowMs - clock.originAt) / config.tickMs;

  const record = (tick, positions, nowMs) => {
    const newest = frames[frames.length - 1];
    if (newest !== undefined) {
      // The tick count going backwards means a different timeline — a new
      // match, or a reset. Playback cannot span two of them, so rather than
      // interpolating a unit from where it stood in the last game to where it
      // stands in this one, start over.
      if (tick < newest.tick) {
        reset();
      } else if (tick === newest.tick) {
        // The same frame twice. Playback walks this list assuming it climbs.
        return;
      }
    }
    frames.push({ tick, positions });
    if (frames.length > config.limit) {
      frames.shift();
    }

    // Where playback should sit: far enough behind the newest snapshot that a
    // late one is already in hand by the time it is needed.
    const target = tick - config.delayMs / config.tickMs;
    const current = tickAt(nowMs);
    if (current === null || Math.abs(target - current) > config.snapTicks) {
      // First snapshot, or so far adrift that easing would take seconds.
      clock = { originTick: target, originAt: nowMs };
      return;
    }
    if (Math.abs(target - current) <= config.deadbandTicks) {
      // Within tolerance. Leave the clock free-running: it already advances at
      // exactly the right rate, and touching it here is how jitter gets back in.
      return;
    }
    // Real drift. Nudge, don't jump.
    clock = {
      originTick: current + (target - current) * config.ease,
      originAt: nowMs,
    };
  };

  // Where a unit should be drawn now. `fallback` is used until the buffer has
  // something to say — before the first snapshot, or for a unit it has never
  // carried.
  const positionAt = (key, nowMs, fallback) => {
    const at = tickAt(nowMs);
    if (at === null || frames.length === 0) {
      return fallback;
    }
    let index = -1;
    for (let i = frames.length - 1; i >= 0; i--) {
      if (frames[i].tick <= at) {
        index = i;
        break;
      }
    }
    // Still filling the buffer: hold the oldest thing we have rather than
    // guessing at where the unit was before we were watching.
    if (index === -1) {
      return frames[0].positions[key] ?? fallback;
    }
    const from = frames[index].positions[key];
    if (from === undefined) {
      return fallback;
    }
    const next = frames[index + 1];
    if (next === undefined) {
      // Starved: nothing newer has arrived, so there is nothing to slide
      // toward. Holding is right — extrapolating would overshoot and then snap
      // back, which looks worse than the pause it replaces.
      return from;
    }
    const to = next.positions[key];
    if (to === undefined) {
      return from;
    }
    if (Math.hypot(to.x - from.x, to.y - from.y) > config.snapDistance) {
      return to;
    }
    const span = next.tick - frames[index].tick;
    const alpha = Math.max(0, Math.min(1, (at - frames[index].tick) / span));
    return {
      x: from.x + (to.x - from.x) * alpha,
      y: from.y + (to.y - from.y) * alpha,
    };
  };

  return { record, positionAt, reset };
};
